'use client';

import {
  ChatMessageActionsSlot,
  ChatMessageAttachmentsSlot,
  ChatMessageContentSlot,
  ChatMessageVariantContext,
} from '@magicschool/spellbook/components/ChatMessage/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ComponentProps, FC } from 'react';

const DEFAULT_SPEAKER = {
  user: 'You said:',
  assistant: 'MagicSchool said:',
} as const;

export const chatMessageVariants = cva(
  [
    'group/chat-message',
    'relative',
    'w-full',
    'min-w-0',
    'outline-none',
    'animate-in',
    'fade-in',
    'duration-350',
    'ease-in-out',
    'motion-reduce:animate-none',
  ],
  {
    variants: {
      /** `user` aligns a bubble sized to its content to the end of the thread; `assistant` renders unbubbled, full-width content. */
      variant: {
        user: ['flex', 'flex-col', 'items-end'],
        assistant: ['flow-root'],
      },
    },
  },
);

export type ChatMessageProps = ComponentProps<'article'> &
  VariantProps<typeof chatMessageVariants> & {
    /** The reply is still being generated: sets `aria-busy` and holds back `ChatMessage.Actions` until it finishes. */
    streaming?: boolean;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: {
      /** Visually hidden line that tells screen-reader users who is speaking. Defaults to "You said:" or "MagicSchool said:". */
      speaker?: string;
    };
  };

const ChatMessageBase: FC<ChatMessageProps> = ({ variant = 'user', streaming = false, text, className, children, ...props }) => {
  const { slots, rest } = useSlots(children, {
    actions: { Component: ChatMessageActionsSlot },
    attachments: { Component: ChatMessageAttachmentsSlot },
    content: { Component: ChatMessageContentSlot },
  });
  const hasRest = rest.some((child) => child != null && child !== false && child !== '');

  return (
    <article
      data-spellbook-name="chat-message"
      data-role={variant ?? 'user'}
      aria-busy={streaming || undefined}
      tabIndex={-1}
      className={cn(chatMessageVariants({ variant }), className)}
      {...props}
    >
      <p className="sr-only">{text?.speaker ?? DEFAULT_SPEAKER[variant ?? 'user']}</p>
      {slots.attachments}
      <ChatMessageVariantContext.Provider value={variant ?? 'user'}>
        {slots.content ? (
          <>
            {slots.content}
            {rest}
          </>
        ) : hasRest ? (
          <ChatMessageContentSlot>{rest}</ChatMessageContentSlot>
        ) : null}
      </ChatMessageVariantContext.Provider>
      {streaming ? null : slots.actions}
    </article>
  );
};

/**
 * One turn in a chat thread: the user's messages as a bubble aligned to the end, the assistant's as full-width content, with the thread semantics and message actions every chat surface uses.
 *
 * ### When to use
 * - Rendering messages in a chat or student thread, alongside `ChatInput`
 *
 * ### When NOT to use
 * - Comments or notes outside a conversation: use `Card` or `Text` instead
 *
 * ### API
 * - `variant`: Who is speaking: `user` (default, end-aligned bubble) or `assistant` (unbubbled, full width). Exposed as `data-role` for styling and tests
 * - `streaming`: While a reply is generating, marks the message busy and holds back its actions
 * - `text.speaker`: Localized speaker line, e.g. the app's `chat.generation_user_header` / `chat.generation_system_header`
 * - `children`: The message content, rendered inside the bubble (`chat-message-content`). Nothing is rendered when there are none
 *
 * ### Composition (slots)
 * - `<ChatMessage.Content>`: The bubble itself, rendered around `children` by default. Pass it explicitly to set `tone` (`non-urgent`, `urgent`) or `width="full"`, or render it on its own (with `variant="user"` inside an assistant message) for a bubble-only part. Once it is explicit, other loose children render after it, outside the bubble
 * - `<ChatMessage.Attachments>`: Files sent with the message, rendered above it and always visible, e.g. an `sm` ghost `Button` with `Button.ToggleIcon` that opens a `Popover` listing them
 * - `<ChatMessage.Actions>`: The message actions, rendered below the message: under the bubble at the end for `user`, and full width under the reply for `assistant`. Hidden until the message is hovered or focused; set `autohide={false}` on the latest message. The actions themselves (copy, read aloud) come from the app; use `sm` neutral `ghost` `IconButton`s with label tooltips
 *
 * ### Accessibility
 * - Renders an `article`, so screen-reader users can move message to message
 * - Starts with a visually hidden speaker line ("You said:" / "MagicSchool said:") so each message announces who said it
 * - Has `tabIndex={-1}`, so the app can move focus to a message (e.g. scroll-to-message) without adding it to the tab order
 * - `aria-busy` while `streaming`, so screen readers wait for the finished reply
 * - Auto-hidden actions stay in the tab order and appear when focus moves into the message; on touch screens they are always shown
 * - The enter animation respects `prefers-reduced-motion`
 *
 * ### Differences from assistant-ui
 * Modeled on assistant-ui's `MessagePrimitive` and `ActionBarPrimitive`, with these deliberate differences:
 * - Styled and presentational, not headless: who is speaking, content, and actions come from props and children rather than a message runtime, and action behavior such as copy stays in the app
 * - Who is speaking is the `variant` prop rather than `message.role`, since a `role` prop would collide with the ARIA `role` attribute; it is still exposed as `data-role`, as in assistant-ui's reference thread
 * - `streaming` on the message replaces the action bar's `hideWhenRunning`
 * - `autohide` is a boolean that defaults to `true`, instead of `"always" | "not-last" | "never"` defaulting to `"never"`. The app sets `autohide={false}` on the latest message to get `"not-last"`, since ChatMessage does not know which message is last
 * - Renders an `article` with a visually hidden speaker line instead of a `div`, for screen-reader navigation
 * - `ChatMessage.Attachments` is a layout slot for the app's own attachment UI, not a runtime-backed list like `MessagePrimitive.Attachments`
 * - `ChatMessage.Content` mirrors `MessagePrimitive.Content` but is the styled bubble, with `tone` and `width` for moderation and long inputs; outside a `ChatMessage` it renders as a user bubble
 * - No branch picker, error, or message-parts primitives
 */
export const ChatMessage = Object.assign(ChatMessageBase, {
  Actions: ChatMessageActionsSlot,
  Content: ChatMessageContentSlot,
  Attachments: ChatMessageAttachmentsSlot,
});
